import React, { useState, useEffect } from "react";
import "./CommittePanel.css";
// NOTE: Do NOT import quill.snow.css here — it's only needed in the admin editor.
// Importing it in the view panel overrides word-wrap styles and causes mid-word line breaks.
import OrgTreeModal from "./OrgTreeModal";
import { getAllPanels, fetchPanelMembers } from "../../../../api/ApiConfig";
import {
  FaUsers,
  FaArrowLeft,
  FaSearch,
  FaTimes,
  FaChevronRight,
  FaBullseye,
  FaBuilding,
  FaSpinner,
  FaExclamationTriangle,
  FaLink,
  FaExternalLinkAlt,
  FaCopy,
  FaCheck,
  FaAt
} from "react-icons/fa";

const GRADIENTS = [
  "linear-gradient(135deg, rgb(179 29 35) 0%, rgb(171 102 49) 99%)",
  "linear-gradient(135deg, rgb(38 29 179) 0%, rgb(59 128 190) 99%)",
  "linear-gradient(135deg, rgb(133 12 158) 0%, rgb(59 71 190) 99%)",
  "linear-gradient(135deg, rgb(13 114 59) 0%, rgb(5 74 24) 99%)",
  "linear-gradient(135deg, rgb(185 23 54) 0%, rgb(12 50 187) 99%)",
  "linear-gradient(135deg, rgb(88 47 11) 0%, rgb(174 109 18) 99%)",
  "linear-gradient(135deg, rgb(20 138 139) 0%, rgb(33 91 108) 100%)",
];

const CommitteePanel = () => {
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [panels, setPanels] = useState([]);
  const [panelMembers, setPanelMembers] = useState({});
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState({});
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [showOrgModal, setShowOrgModal] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [copiedItem, setCopiedItem] = useState(null);

  // Track copied DL with an ID so only the correct button shows "Copied!"
  const [copiedPanelId, setCopiedPanelId] = useState(null);

  const getInitials = (name) => {
    if (!name) return "XX";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const renderPurpose = (text) => {
    if (!text || !text.trim()) {
      return <p style={{ color: "#94a3b8" }}>No purpose defined.</p>;
    }

    const textStyle = {
      color: "#4a5568",
      lineHeight: "1.7",
      fontSize: "0.875rem",
      wordBreak: "normal",
      overflowWrap: "break-word",
      hyphens: "none",
      WebkitHyphens: "none",
      whiteSpace: "normal",
      maxWidth: "100%",
    };

    const isHTML = /<[a-z][\s\S]*>/i.test(text);

    if (!isHTML) {
      const normalized = text.replace(/\r?\n/g, " ").replace(/\s+/g, " ").trim();
      return <p style={{ ...textStyle, margin: 0 }}>{normalized}</p>;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, "text/html");

      const domToReact = (node, key) => {
        if (node.nodeType === 3) {
          return node.textContent.replace(/\r?\n/g, " ").replace(/\s+/g, " ");
        }

        if (node.nodeType !== 1) return null;

        const tag = node.tagName.toLowerCase();
        const children = Array.from(node.childNodes).map((c, i) => domToReact(c, i));

        switch (tag) {
          case "p": return <p key={key} style={{ ...textStyle, margin: "0 0 8px 0" }}>{children}</p>;
          case "ol": return <ol key={key} style={{ paddingLeft: "20px", margin: "8px 0", maxWidth: "100%" }}>{children}</ol>;
          case "ul": return <ul key={key} style={{ paddingLeft: "20px", margin: "8px 0", maxWidth: "100%" }}>{children}</ul>;
          case "li": return <li key={key} style={{ ...textStyle, marginBottom: "4px" }}>{children}</li>;
          case "strong":
          case "b": return <strong key={key}>{children}</strong>;
          case "em":
          case "i": return <em key={key}>{children}</em>;
          case "u": return <u key={key}>{children}</u>;
          case "br": return <br key={key} />;
          default: return <React.Fragment key={key}>{children}</React.Fragment>;
        }
      };

      return (
        <div className="cpanel-purpose-render">
          {Array.from(doc.body.childNodes).map((node, i) => domToReact(node, i))}
        </div>
      );
    } catch {
      const plain = text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
      return <p style={{ ...textStyle, margin: 0 }}>{plain}</p>;
    }
  };

  useEffect(() => {
    fetchPanels();
  }, []);

  useEffect(() => {
    if (selectedPanel && panels.length > 0) {
      fetchMembers(selectedPanel);
    }
  }, [selectedPanel, panels]);

  const fetchPanels = async () => {
    setLoading(true);
    try {
      const response = await getAllPanels();
      const rawData = Array.isArray(response) ? response : response?.data || [];

      const data = rawData.map((p, index) => ({
        id: p.id || p.panel_id,
        panel_name: p.panel_name || p.name || " ",
        members_count: p.members_count || p.member_count || 0,
        panel_description: p.panel_description || p.description || "",
        panel_purpose: p.panel_purpose || p.purpose || " ",
        // DL FIELD
        dl_mail_id: p.dl_mail_id,
        theme: GRADIENTS[index % GRADIENTS.length],
        sop_link: p.sop_link,
      }));


      setPanels(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load panels. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async (panelId) => {
    if (panelMembers[panelId]) return;
    setMembersLoading((prev) => ({ ...prev, [panelId]: true }));

    try {
      const response = await fetchPanelMembers({ panel_id: panelId });
      let data = [];

      if (response?.data && Array.isArray(response.data)) {
        data = response.data;
      } else if (Array.isArray(response)) {
        data = response;
      } else if (response?.members) {
        data = response.members;
      }

      const members = data.map((m) => ({
        id: m.member_id || `${panelId}_${m.emp_id}`,
        emp_id: m.emp_id,
        name: m.emp_name || "Unknown Member",
        role: m.member_role || "Member",
        department: m.department || "General",
        email: m.email || "N/A",
        initials: getInitials(m.emp_name),
      }));

      setPanelMembers((prev) => ({ ...prev, [panelId]: members }));
    } catch (err) {
      console.error(err);
      setPanelMembers((prev) => ({ ...prev, [panelId]: [] }));
    } finally {
      setMembersLoading((prev) => ({ ...prev, [panelId]: false }));
    }
  };

  const handleBack = () => {
    setSelectedPanel(null);
    window.scrollTo(0, 0);
  };

  // Location ID ensures we only show "Copied!" on the exact button clicked
  const handleCopyDL = (e, dlText, locationId) => {
    e.stopPropagation();

    navigator.clipboard.writeText(dlText);

    setCopiedItem(locationId);

    setTimeout(() => {
      setCopiedItem(null);
    }, 2000);
  };

  // =========================================
  // PANEL CARD (Dashboard View)
  // =========================================
  const renderCard = (panel) => {
    const hasDesc = panel.panel_description && panel.panel_description.trim().length > 0;
    const cardId = `card-${panel.id}`;

    return (
      <div
        key={panel.id}
        className="cpanel-card"
        onClick={() => setSelectedPanel(panel.id)}
      >
        {/* TOP BAR */}
        <div className="cpanel-accent-bar" style={{ background: panel.theme }}></div>

        {/* CONTENT */}
        <div className="cpanel-content">
          <div className="cpanel-top-row">
            <div className="cpanel-main-icon-wrapper" style={{ background: panel.theme }}>
              <FaUsers />
            </div>
            <div className="cpanel-badge">{panel.members_count} Members</div>
          </div>

          <div className={`cpanel-text-wrapper ${!hasDesc ? "no-desc" : ""}`}>
            <h3 className="cpanel-card-title">{panel.panel_name}</h3>
            {hasDesc && <div className="cpanel-card-desc">{panel.panel_description}</div>}
          </div>
        </div>

        {/* FOOTER */}
        <div className="cpanel-card-footer">
          <div className="cpanel-footer-left">
            {panel.dl_mail_id && (
              <button
                className={`cpanel-dl-button ${copiedItem === `${cardId}-dl` ? "copied" : ""
                  }`}
                onClick={(e) =>
                  handleCopyDL(e, panel.dl_mail_id, `${cardId}-dl`)
                }
                title="Click to copy DL"
              >
                {copiedItem === `${cardId}-dl` ? <FaCheck /> : <FaCopy />}
                <span>
                  {copiedItem === `${cardId}-dl` ? "Copied!" : "DL"}
                </span>
              </button>
            )}

            {panel.sop_link && (
              <button
                className={`cpanel-dl-button ${copiedItem === `${cardId}-sop` ? "copied" : ""
                  }`}
                onClick={(e) =>
                  handleCopyDL(e, panel.sop_link, `${cardId}-sop`)
                }
                title="Click to copy SOP"
              >
                {copiedItem === `${cardId}-sop` ? <FaCheck /> : <FaCopy />}
                <span>
                  {copiedItem === `${cardId}-sop` ? "Copied!" : "SOP"}
                </span>
              </button>
            )}
          </div>

          <div className="cpanel-arrow-btn">
            <FaChevronRight />
          </div>
        </div>
      </div>
    );
  };

  // =========================================
  // DASHBOARD
  // =========================================
  const renderDashboard = () => {
    const filtered = panels
      .filter((p) => p.panel_name.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort((a, b) => a.panel_name.localeCompare(b.panel_name));

    const isCohort = (name) => name?.toLowerCase().includes("cohort");
    const committeesPanels = filtered.filter((p) => !isCohort(p.panel_name));
    const cohorts = filtered.filter((p) => isCohort(p.panel_name));

    return (
      <div className="cpanel-header-section">
        <h1 className="cpanel-main-title">Committees / Panels / Cohorts</h1>
        <p className="cpanel-subtitle">Select a category below to view its objectives and members.</p>

        <div className="cpanel-search-container">
          <div className="cpanel-search-box">
            <FaSearch className="cpanel-search-icon" />
            <input
              type="text"
              className="cpanel-search-input"
              placeholder="Search ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="cpanel-search-clear" onClick={() => setSearchTerm("")}>
                <FaTimes />
              </button>
            )}
          </div>
        </div>

        <h2 className="cpanel-section-heading">Committees & Panels</h2>
        <div className="cpanel-scroll-container">
          <div className="cpanel-grid">
            {committeesPanels.map((panel) => renderCard(panel))}
            {committeesPanels.length === 0 && <div className="cpanel-no-results">No committees found.</div>}
          </div>
        </div>

        <div className="cpanel-section-divider"></div>

        <h2 className="cpanel-section-heading">Cohorts</h2>
        <div className="cpanel-scroll-container">
          <div className="cpanel-grid">
            {cohorts.map((panel) => renderCard(panel))}
            {cohorts.length === 0 && <div className="cpanel-no-results">No cohorts found.</div>}
          </div>
        </div>
      </div>
    );
  };

  // =========================================
  // DETAILS
  // =========================================
  const renderDetails = () => {
    const panel = panels.find((p) => p.id === selectedPanel);
    if (!panel) return null;


    const theme = panel.theme;
    const detailsId = `details-${panel.id}`;
    const members = panelMembers[selectedPanel] || [];
    const sortedMembers = [...members].sort((a, b) => a.name.localeCompare(b.name));

    return (
      <div className="cpanel-details-container">
        <div className="cpanel-details-header-card" style={{ background: theme }}>

          <button className="cpanel-back-button" onClick={handleBack}>
            <FaArrowLeft /> Back
          </button>

          {/* 🔥 DL Email Card 🔥 */}
          <div className="cpanel-dl-email-card">
            {panel.dl_mail_id ? (
              <>
                <div className="cpanel-dl-email-left">
                  <div className="cpanel-dl-email-icon">
                    <FaAt />
                  </div>

                  <div className="cpanel-dl-email-text-box">
                    <span className="cpanel-dl-email-label">DL</span>
                    <span className="cpanel-dl-email-value">
                      {panel.dl_mail_id}
                    </span>
                  </div>
                </div>

                <button
                  className={`cpanel-dl-email-copy-btn ${copiedPanelId === detailsId ? "copied" : ""
                    }`}
                  onClick={(e) =>
                    handleCopyDL(e, panel.dl_mail_id, detailsId)
                  }
                >
                  {copiedPanelId === detailsId ? (
                    <>
                      <FaCheck /> Copied
                    </>
                  ) : (
                    <>
                      <FaCopy /> Copy
                    </>
                  )}
                </button>
              </>
            ) : (
              <div className="cpanel-dl-empty-state">
                <FaAt className="cpanel-dl-empty-icon" />
                <span>No DL Available</span>
              </div>
            )}
          </div>




          <h1 className="cpanel-details-title">{panel.panel_name}</h1>
          <p className="cpanel-details-subtitle">{panel.panel_description}</p>
        </div>

        <div className="cpanel-details-stack">

          {/* PURPOSE */}
          <div className="cpanel-details-section">
            <div className="cpanel-details-section-header">
              <div className="cpanel-section-icon" style={{ background: theme }}><FaBullseye /></div>
              Purpose & Objectives
            </div>
            <div className="cpanel-purpose-content">
              {renderPurpose(panel.panel_purpose)}
            </div>
          </div>

          {/* SOP */}
          <div className="cpanel-details-section">
            <div className="cpanel-details-section-header">
              <div className="cpanel-section-icon" style={{ background: theme }}><FaLink /></div>
              SOP Document
            </div>
            <div className="cpanel-purpose-content" style={{ padding: "10px 0" }}>
              {panel.sop_link ? (
                <a
                  href={panel.sop_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "10px 20px",
                    background: theme || "#007bff",
                    color: "#ffffff",
                    textDecoration: "none",
                    borderRadius: "8px",
                    fontWeight: "600",
                    fontSize: "14px",
                  }}
                >
                  <FaExternalLinkAlt /> Open SOP
                </a>
              ) : (
                <button disabled>No Link Available</button>
              )}
            </div>
          </div>

          {/* MEMBERS */}
          <div className="cpanel-details-section">
            <div className="cpanel-details-section-header">
              <div className="cpanel-section-icon" style={{ background: theme }}><FaUsers /></div>
              Members
              <span className="cpanel-member-count-badge">{sortedMembers.length}</span>
            </div>

            {sortedMembers.length > 0 ? (
              <div className="cpanel-members-grid">
                {sortedMembers.map((member) => (
                  <div
                    key={member.id}
                    className="cpanel-member-card"
                    onClick={() => {
                      setSelectedMemberId(member.emp_id);
                      setShowOrgModal(true);
                    }}
                  >
                    <div className="cpanel-member-avatar" style={{ background: theme }}>{member.initials}</div>
                    <div style={{ flex: 1 }}>
                      <div className="cpanel-member-name">{member.name}</div>
                      <div className="cpanel-member-role">{member.role}</div>
                      <div className="cpanel-member-info-grid">
                        <div className="cpanel-info-item">
                          <FaBuilding className="cpanel-info-icon" />
                          {member.department}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="cpanel-no-members-state">
                <p>No members added yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="cpanel-container">
        <div className="cpanel-loading-container">
          <FaSpinner className="cpanel-spinner" />
          <h3>Loading ...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="cpanel-container">
      {error && (
        <div className="cpanel-error-banner">
          <span>
            <FaExclamationTriangle style={{ marginRight: "10px" }} />
            {error}
          </span>
          <FaTimes className="cpanel-close-error" onClick={() => setError(null)} />
        </div>
      )}

      {!selectedPanel ? renderDashboard() : renderDetails()}

      {showOrgModal && (
        <OrgTreeModal
          empId={selectedMemberId}
          onClose={() => setShowOrgModal(false)}
        />
      )}
    </div>
  );
};

export default CommitteePanel;